/**
 * 🛣️ ERROR PAGES ROUTER
 * Centralized routing configuration for all error pages
 */
import React from 'react';
import { Routes, Route } from 'react-router-dom';
// Importer directement pour éviter un cycle avec ./index
import NotFoundPage from './NotFoundPage';
import ForbiddenPage from './ForbiddenPage';
import ServerErrorPage from './ServerErrorPage';
import GenericErrorPage from './GenericErrorPage';

/**
 * Error Pages Router Component
 * Handles routing for all error pages with proper status codes
 */
const ErrorPagesRouter: React.FC = () => {
  return (
    <Routes>
      {/* 404 - Not Found */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="/not-found" element={<NotFoundPage />} />
      
      {/* 403 - Forbidden */}
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/forbidden" element={<ForbiddenPage />} />
      <Route path="/access-denied" element={<ForbiddenPage />} />
      
      {/* 500 - Server Error */}
      <Route path="/500" element={<ServerErrorPage />} />
      <Route path="/server-error" element={<ServerErrorPage />} />
      <Route path="/internal-error" element={<ServerErrorPage />} />
      
  {/* Generic error pages */}
      <Route 
        path="/error/400" 
        element={
          <GenericErrorPage 
            statusCode={400}
            title="Requête Incorrecte"
            message="Les données envoyées sont invalides ou incomplètes."
          />
        } 
      />
      
      <Route 
        path="/error/401" 
        element={
          <GenericErrorPage 
            statusCode={401}
            title="Authentification Requise"
            message="Vous devez vous connecter pour accéder à cette ressource."
          />
        } 
      />
      
      <Route 
        path="/error/408" 
        element={
          <GenericErrorPage 
            statusCode={408}
            title="Délai d'Attente Dépassé"
            message="La requête a pris trop de temps à être traitée."
          />
        } 
      />
      
      <Route 
        path="/error/429" 
        element={
          <GenericErrorPage 
            statusCode={429}
            title="Trop de Requêtes"
            message="Vous avez effectué trop de requêtes. Veuillez patienter."
          />
        } 
      />
      
      <Route 
        path="/error/502" 
        element={
          <GenericErrorPage 
            statusCode={502}
            title="Passerelle Défaillante"
            message="Erreur de communication avec le serveur."
          />
        } 
      />
      
      <Route 
        path="/error/503" 
        element={
          <GenericErrorPage 
            statusCode={503}
            title="Service Indisponible"
            message="Le service est temporairement indisponible."
          />
        } 
      />
      
      {/* Maintenance Mode */}
      <Route 
        path="/maintenance" 
        element={
          <GenericErrorPage 
            statusCode={503}
            title="Maintenance en Cours"
            message="Le site est actuellement en maintenance. Nous serons de retour bientôt."
            icon="🔧"
            showRetry={false}
            suggestions={[
              'La maintenance devrait se terminer sous peu',
              'Consultez nos réseaux sociaux pour les mises à jour',
              'Merci de votre patience'
            ]}
          />
        } 
      />
      
      {/* Catch-all route for unhandled errors */}
      <Route path="/error/*" element={<NotFoundPage />} />
    </Routes>
  );
};

/**
 * 🎯 ERROR NAVIGATION UTILITIES
 * Helper functions for navigating to error pages
 */
export const navigateToError = {
  notFound: () => '/404',
  forbidden: () => '/403',
  serverError: () => '/500',
  badRequest: () => '/error/400',
  unauthorized: () => '/error/401',
  timeout: () => '/error/408',
  tooManyRequests: () => '/error/429',
  badGateway: () => '/error/502',
  serviceUnavailable: () => '/error/503',
  maintenance: () => '/maintenance',
  generic: (statusCode: number) => `/error/${statusCode}`
};

/**
 * 🔧 ERROR PAGE CONFIGURATION
 * Configuration object for error pages
 */
export const errorPageConfig = {
  // Whether to show technical details in production
  showTechnicalDetails: import.meta.env.DEV,
  
  // Default retry attempts for auto-retry functionality
  maxRetries: 3,
  retryDelay: 2000,
  
  // Support contact information
  supportEmail: 'jf.c.webcraft@gmail.com',
  supportPhone: '+33 1 23 45 67 89',
  
  // Error tracking configuration
  enableErrorTracking: true,
  errorTrackingUrl: import.meta.env.VITE_ERROR_TRACKING_URL,
  
  // Analytics tracking for error pages
  enableAnalytics: true,
  analyticsCategory: 'Error Pages'
};

export default ErrorPagesRouter;
